import { useCallback, useEffect, useRef, useState } from 'react';

interface UseVisibilityObserverOptions {
  /**
   * Root margin for the IntersectionObserver.
   * Positive values expand the root's bounding box, triggering earlier.
   * e.g., "200px 0px" means trigger when element is 200px from entering viewport
   * @default "200px 0px 200px 0px"
   */
  rootMargin?: string;

  /**
   * Whether lazy loading is enabled. When false, element is considered
   * immediately visible (for non-ClickHouse sessions).
   * @default true
   */
  enabled?: boolean;

  /**
   * Delay in ms before setting up the observer.
   * Allows layout engines (like react-grid-layout) to position elements first.
   * @default 100
   */
  setupDelay?: number;
}

interface UseVisibilityObserverResult {
  /**
   * Ref callback to attach to the element you want to observe
   */
  ref: (node: HTMLDivElement | null) => void;

  /**
   * Whether the element is currently visible in the viewport.
   * Updates in real-time as user scrolls.
   */
  isCurrentlyVisible: boolean;

  /**
   * Whether the element has ever been visible.
   * Once true, stays true (prevents unloading on scroll)
   */
  hasBeenVisible: boolean;
}

/**
 * Hook to detect when an element becomes visible in the viewport.
 * Uses IntersectionObserver for efficient visibility detection.
 *
 * Used for lazy loading A-level widgets in ClickHouse sessions to reduce
 * concurrent queries and improve initial page load performance.
 *
 * @example
 * ```tsx
 * const { ref, hasBeenVisible } = useVisibilityObserver({
 *   enabled: enableClickhouse,
 *   rootMargin: "200px 0px",
 * });
 *
 * return (
 *   <div ref={ref}>
 *     {hasBeenVisible ? <ActualWidget /> : <WidgetSkeleton />}
 *   </div>
 * );
 * ```
 */
const useVisibilityObserver = ({
  rootMargin = '200px 0px 200px 0px',
  enabled = true,
  setupDelay = 100,
}: UseVisibilityObserverOptions = {}): UseVisibilityObserverResult => {
  const [isCurrentlyVisible, setIsCurrentlyVisible] = useState(false);
  // Start as false - we'll determine visibility based on observer or enabled flag
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  // Track if lazy loading was ever enabled (to prevent flip-flopping)
  const lazyLoadEnabledRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const setupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if initial visibility check has passed (to avoid false positives during layout)
  const initialCheckPassedRef = useRef(false);

  // Cleanup observer and any pending setup
  const cleanupObserver = useCallback(() => {
    if (setupTimeoutRef.current) {
      clearTimeout(setupTimeoutRef.current);
      setupTimeoutRef.current = null;
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  // Setup observer on element (called after delay)
  const createAndStartObserver = useCallback(
    (node: HTMLDivElement) => {
      // Check if IntersectionObserver is supported
      if (!('IntersectionObserver' in window)) {
        // Fallback: immediately mark as visible for older browsers
        setIsCurrentlyVisible(true);
        setHasBeenVisible(true);
        return;
      }

      // Create observer that tracks visibility continuously
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          const isIntersecting = entry.isIntersecting;

          // For the first visibility check, require threshold to be met
          // After that, track visibility in real-time
          if (!initialCheckPassedRef.current) {
            if (isIntersecting) {
              initialCheckPassedRef.current = true;
              setIsCurrentlyVisible(true);
              setHasBeenVisible(true);
            }
          } else {
            // After initial load, track visibility changes for filter updates
            setIsCurrentlyVisible(isIntersecting);
          }
        },
        {
          root: null, // Use viewport as root
          rootMargin,
          threshold: 0.1, // Require 10% visibility to avoid initial stacking issues
        }
      );

      observerRef.current.observe(node);
    },
    [rootMargin]
  );

  // Schedule observer setup with delay (allows layout to stabilize)
  const scheduleObserverSetup = useCallback(
    (node: HTMLDivElement) => {
      // Clear any existing scheduled setup
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current);
      }

      // Schedule the observer setup after a delay
      // This allows react-grid-layout to position elements first
      setupTimeoutRef.current = setTimeout(() => {
        setupTimeoutRef.current = null;
        if (elementRef.current === node) {
          createAndStartObserver(node);
        }
      }, setupDelay);
    },
    [setupDelay, createAndStartObserver]
  );

  // Callback ref that sets up the observer when element is attached
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      // Cleanup previous observer
      cleanupObserver();
      elementRef.current = node;
      initialCheckPassedRef.current = false;

      // If no node, nothing to observe
      if (!node) {
        return;
      }

      // If lazy loading is disabled, mark as visible immediately
      // But only if we haven't already started lazy loading
      if (!enabled && !lazyLoadEnabledRef.current) {
        setIsCurrentlyVisible(true);
        setHasBeenVisible(true);
        initialCheckPassedRef.current = true;
        return;
      }

      // If enabled, track that lazy loading has been activated
      if (enabled) {
        lazyLoadEnabledRef.current = true;
      }

      // Schedule the observer setup with delay
      scheduleObserverSetup(node);
    },
    [enabled, cleanupObserver, scheduleObserverSetup]
  );

  // Handle enabled flag changing to true after initial mount
  // This handles the case where enable_clickhouse loads from API after component mounts
  useEffect(() => {
    if (enabled && !lazyLoadEnabledRef.current && elementRef.current && !hasBeenVisible) {
      lazyLoadEnabledRef.current = true;
      // Re-setup observer now that lazy loading is enabled
      cleanupObserver();
      scheduleObserverSetup(elementRef.current);
    }
  }, [enabled, hasBeenVisible, cleanupObserver, scheduleObserverSetup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupObserver();
    };
  }, [cleanupObserver]);

  return {
    ref,
    isCurrentlyVisible,
    hasBeenVisible,
  };
};

export { useVisibilityObserver };
export type { UseVisibilityObserverOptions, UseVisibilityObserverResult };
